import * as React from 'react';
import './PortableChart.css';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { selectGraphResults, selectGraphSpinner } from '../../selectors';
import Flex from '../../../../components/Flex/Flex';
import Loader from '../../../../components/Loader/Loader';
import { Line, LineChart, ReferenceLine, ResponsiveContainer } from 'recharts';
import Text from '../../../../components/Text/Text';

const SMOOTH_LINE = {
  type: 'natural' as const,
  dot: false,
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const PortableChart = React.memo(({ mini = false }: { mini?: boolean }) => {
  const graphResults = useSelector(selectGraphResults);
  const loading = useSelector(selectGraphSpinner);

  if (loading) {
    return (
      <Flex
        className={classNames('PortableChart__Loading', {
          'PortableChart--mini': mini,
        })}
        justify="center"
        align="center"
      >
        <Loader />
      </Flex>
    );
  }

  if (!graphResults) return null;

  const { result } = graphResults;

  if (result.length === 0) {
    return (
      <Flex
        className={classNames('PortableChart', {
          'PortableChart--mini': mini,
        })}
        justify="center"
        align="center"
      >
        <Text>-</Text>
      </Flex>
    );
  }

  const data = result.reduce<
    Array<{
      result: number;
      resultWithShowdown: number;
      resultWithoutShowdown: number;
    }>
  >(
    (acc, _res, index) => {
      acc.push({
        result: acc[acc.length - 1].result + graphResults.result[index],
        resultWithShowdown:
          acc[acc.length - 1].resultWithShowdown + graphResults.resultWithShowdown[index],
        resultWithoutShowdown:
          acc[acc.length - 1].resultWithoutShowdown + graphResults.resultWithoutShowdown[index],
      });
      return acc;
    },
    [{ result: 0, resultWithShowdown: 0, resultWithoutShowdown: 0 }],
  );

  const lineProps = mini ? { ...SMOOTH_LINE, strokeWidth: 1 } : SMOOTH_LINE;

  return (
    <div
      className={classNames('PortableChart', {
        'PortableChart--mini': mini,
      })}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <ReferenceLine y={0} stroke="var(--portable-chart-zero, #484d57)" />
          <Line
            {...lineProps}
            dataKey="resultWithShowdown"
            stroke="var(--portable-chart-wsd, var(--color-primary))"
          />
          <Line
            {...lineProps}
            dataKey="resultWithoutShowdown"
            stroke="var(--portable-chart-wo, var(--color-red-6))"
          />
          <Line
            {...lineProps}
            dataKey="result"
            stroke="var(--portable-chart-result, var(--color-green-6))"
          />
          <ReferenceLine
            y={0}
            stroke="var(--portable-chart-zero-line, rgba(255, 255, 255, 0.35))"
            strokeWidth={mini ? 1 : 1.5}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

export default PortableChart;
